// @ts-nocheck
const VISUAL_BLOCK_TYPES = new Set(['image', 'imageSlider']);

function localizedText(value) {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') return value.en || value.fr || '';
  return '';
}

function escapeSvg(value) {
  return String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function wrapSvgText(value, maxChars, maxLines) {
  const words = String(value || '').trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (line && next.length > maxChars) {
      lines.push(line);
      line = word;
    } else line = next;
  });
  if (line) lines.push(line);
  if (lines.length <= maxLines) return lines;
  const clipped = lines.slice(0, maxLines);
  clipped[maxLines - 1] = `${clipped[maxLines - 1].slice(0, Math.max(0, maxChars - 1)).trim()}…`;
  return clipped;
}

function svgText(lines, x, y, { size, fill, weight = 400, lineHeight = 24, family = 'Arial,sans-serif' }) {
  return `<text x="${x}" y="${y}" fill="${fill}" font-family="${family}" font-size="${size}" font-weight="${weight}">${lines.map((line, index) => `<tspan x="${x}" dy="${index ? lineHeight : 0}">${escapeSvg(line)}</tspan>`).join('')}</text>`;
}

function chapterPrompt(chapter) {
  const prompt = chapter.blocks.find((block) => block.type === 'prompt');
  return localizedText(prompt?.text).replace(/\s+/g, ' ').trim();
}

function chapterCheck(chapter) {
  const test = chapter.blocks.find((block) => block.type === 'visibleTest');
  return localizedText(test?.checks?.[0]) || 'Check the visible result yourself.';
}

function chapterEvidence(chapter) {
  const evidenceBlock = chapter.blocks.find((block) => block.type === 'evidence');
  const artifact = localizedText(evidenceBlock?.artifact || chapter.evidence?.label || chapter.evidence?.placeholder);
  return artifact ? `Save proof: ${artifact}` : 'Save the proof and name the next owner action.';
}

function visualLayout(chapter) {
  const id = String(chapter.id || '').toLowerCase();
  const requiredLayouts = {
    'architecture-decision': 'decision',
    'data-decision': 'states',
    'standard-building-blocks': 'matrix',
    'real-boundary': 'process',
    'analyst-folder': 'screen',
  };
  if (requiredLayouts[id]) return requiredLayouts[id];
  if (/(decision|architecture|data|provider|storage|access|standard-building-blocks|scope|choice)/.test(id)) return 'decision';
  if (/(checkpoint|reopen|journey|flow|publish|handoff|demo|loop|sequence)/.test(id)) return 'process';
  if (/(failure|error|safe|security|state|audit|test|validate|sample|quality)/.test(id)) return 'states';
  if (/(metric|report|matrix|compare|review|evidence)/.test(id)) return 'matrix';
  return 'screen';
}

function numberedNode({ x, y, number, label, theme, width = 196 }) {
  return `<g><circle cx="${x}" cy="${y}" r="27" fill="${theme.accent}"/><text x="${x}" y="${y + 6}" fill="#fff" font-family="Arial,sans-serif" font-size="17" font-weight="700" text-anchor="middle">${number}</text><rect x="${x - width / 2}" y="${y + 46}" width="${width}" height="74" rx="14" fill="${theme.soft}" stroke="#d8dee7"/>${svgText(wrapSvgText(label, 21, 2), x - width / 2 + 18, y + 74, { size: 15, fill: theme.ink, weight: 700, lineHeight: 19 })}</g>`;
}

function renderDecisionVisual(nodes, y, theme) {
  const options = nodes.slice(0, 3);
  const cards = options.map((node, index) => {
    const x = 142 + index * 324;
    const selected = index === 0;
    return `<g><rect x="${x}" y="${y}" width="264" height="190" rx="20" fill="${selected ? theme.soft : '#fff'}" stroke="${selected ? theme.accent : '#d8dee7'}" stroke-width="${selected ? 3 : 1}"/><text x="${x + 28}" y="${y + 40}" fill="${theme.accent}" font-family="Arial,sans-serif" font-size="13" font-weight="700">${selected ? 'START HERE' : `OPTION ${index + 1}`}</text>${svgText(wrapSvgText(node, 22, 3), x + 28, y + 82, { size: 20, fill: theme.ink, weight: 700, lineHeight: 25 })}<rect x="${x + 28}" y="${y + 148}" width="${selected ? 138 : 116}" height="28" rx="14" fill="${selected ? theme.accent : '#eef2f6'}"/><text x="${x + 44}" y="${y + 167}" fill="${selected ? '#fff' : '#687081'}" font-family="Arial,sans-serif" font-size="12" font-weight="700">${selected ? 'SMALLEST FIT' : 'REVIEW FIRST'}</text></g>`;
  }).join('');
  return `<text x="110" y="${y - 24}" fill="#687081" font-family="Arial,sans-serif" font-size="14" font-weight="700">MAKE THE SMALLEST CHOICE THAT CAN PROVE THE WORKFLOW</text>${cards}`;
}

function renderProcessVisual(nodes, y, theme) {
  const steps = nodes.slice(0, 4);
  const start = 196;
  const gap = steps.length > 1 ? 808 / (steps.length - 1) : 0;
  const connectors = steps.slice(0, -1).map((_, index) => {
    const x = start + gap * index;
    return `<path d="M${x + 34} ${y + 28}H${x + gap - 34}" stroke="${theme.accent}" stroke-width="4" stroke-linecap="round"/><path d="M${x + gap - 48} ${y + 17}l14 11-14 11" fill="none" stroke="${theme.accent}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`;
  }).join('');
  return `<text x="110" y="${y - 22}" fill="#687081" font-family="Arial,sans-serif" font-size="14" font-weight="700">FOLLOW THE PROOF IN ORDER</text>${connectors}${steps.map((node, index) => numberedNode({ x: start + gap * index, y: y + 28, number: index + 1, label: node, theme })).join('')}`;
}

function renderStateVisual(nodes, y, theme) {
  const states = nodes.slice(0, 5);
  return `<text x="110" y="${y - 22}" fill="#687081" font-family="Arial,sans-serif" font-size="14" font-weight="700">MAKE EVERY IMPORTANT STATE VISIBLE</text>${states.map((node, index) => {
    const x = 116 + (index % 3) * 334;
    const row = Math.floor(index / 3);
    const stateY = y + row * 104;
    return `<g><rect x="${x}" y="${stateY}" width="302" height="78" rx="16" fill="${index === 3 ? '#fff' : theme.soft}" stroke="${index === 3 ? theme.accent : '#d8dee7'}"/><circle cx="${x + 28}" cy="${stateY + 39}" r="10" fill="${index === 3 ? theme.accent : '#94a3b8'}"/><text x="${x + 52}" y="${stateY + 31}" fill="${theme.accent}" font-family="Arial,sans-serif" font-size="11" font-weight="700">${index === 3 ? 'RECOVERY' : 'CHECK'}</text>${svgText(wrapSvgText(node, 28, 2), x + 52, stateY + 55, { size: 16, fill: theme.ink, weight: 700, lineHeight: 18 })}</g>`;
  }).join('')}`;
}

function renderMatrixVisual(nodes, y, theme) {
  const cells = nodes.slice(0, 4);
  return `<text x="110" y="${y - 22}" fill="#687081" font-family="Arial,sans-serif" font-size="14" font-weight="700">CHECK THE FOUR SIGNALS BEFORE YOU MOVE ON</text><rect x="112" y="${y}" width="976" height="252" rx="20" fill="#fff" stroke="#d8dee7"/><path d="M600 ${y + 18}V${y + 234}M130 ${y + 126}H1070" stroke="#d8dee7"/>${cells.map((node, index) => {
    const column = index % 2;
    const row = Math.floor(index / 2);
    const x = 146 + column * 486;
    const cellY = y + 35 + row * 126;
    return `<g><rect x="${x}" y="${cellY}" width="34" height="24" rx="12" fill="${theme.soft}"/><text x="${x + 17}" y="${cellY + 17}" fill="${theme.accent}" font-family="Arial,sans-serif" font-size="12" font-weight="700" text-anchor="middle">${index + 1}</text>${svgText(wrapSvgText(node, 36, 2), x + 52, cellY + 19, { size: 18, fill: theme.ink, weight: 700, lineHeight: 22 })}</g>`;
  }).join('')}`;
}

function renderScreenVisual(nodes, y, theme) {
  const [primary, ...actions] = nodes;
  return `<g><rect x="112" y="${y}" width="594" height="250" rx="22" fill="#fff" stroke="#d8dee7"/><rect x="112" y="${y}" width="594" height="46" rx="22" fill="${theme.soft}"/><circle cx="144" cy="${y + 23}" r="6" fill="${theme.accent}"/><circle cx="164" cy="${y + 23}" r="6" fill="#94a3b8"/><text x="194" y="${y + 28}" fill="${theme.ink}" font-family="Arial,sans-serif" font-size="13" font-weight="700">ONE CLEAR WORKING SCREEN</text>${svgText(wrapSvgText(primary || 'Start with one useful input.', 45, 3), 150, y + 98, { size: 22, fill: theme.ink, weight: 700, lineHeight: 27 })}<rect x="150" y="${y + 172}" width="500" height="42" rx="10" fill="#f8fafc" stroke="#d8dee7"/><text x="174" y="${y + 199}" fill="#687081" font-family="Arial,sans-serif" font-size="14">Visible input or result</text></g><g><rect x="750" y="${y}" width="338" height="250" rx="22" fill="#17131d"/><text x="784" y="${y + 44}" fill="#c4b5fd" font-family="Arial,sans-serif" font-size="12" font-weight="700">PROOF PANEL</text>${actions.slice(0, 3).map((node, index) => `<g><circle cx="786" cy="${y + 82 + index * 48}" r="11" fill="${theme.accent}"/><text x="786" y="${y + 87 + index * 48}" fill="#fff" font-family="Arial,sans-serif" font-size="11" font-weight="700" text-anchor="middle">${index + 1}</text>${svgText(wrapSvgText(node, 25, 1), 810, y + 87 + index * 48, { size: 14, fill: '#f8fafc', weight: 700 })}</g>`).join('')}</g>`;
}

function createVisualSvg({ chapter, day, slot }) {
  const title = localizedText(chapter.title) || 'Training lesson';
  const objective = localizedText(chapter.objective) || localizedText(chapter.explanation) || 'Make one bounded change and keep proof visible.';
  const prompt = chapterPrompt(chapter) || 'Build only the smallest useful next step.';
  const check = chapterCheck(chapter);
  const evidence = chapterEvidence(chapter);
  const titleLines = wrapSvgText(title, 52, 2);
  const objectiveLines = wrapSvgText(objective, 86, 2);
  const objectiveY = 142 + titleLines.length * 38 + 14;
  const contentY = Math.max(270, objectiveY + objectiveLines.length * 24 + 32);
  const layout = visualLayout(chapter);
  const theme = layout === 'states' ? { accent: '#b65e6b', soft: '#f9e9ec', ink: '#552631' }
    : layout === 'matrix' ? { accent: '#805aa4', soft: '#f2ebf7', ink: '#342044' }
      : layout === 'process' ? { accent: '#4f946f', soft: '#e8f3ec', ink: '#173d30' }
        : { accent: '#2f6f9f', soft: '#e8f1f7', ink: '#142a4d' };
  const nodes = [objective, prompt, check, evidence];
  const visual = layout === 'decision' ? renderDecisionVisual(nodes, contentY, theme)
    : layout === 'process' ? renderProcessVisual(nodes, contentY + 18, theme)
      : layout === 'states' ? renderStateVisual(nodes, contentY + 16, theme)
        : layout === 'matrix' ? renderMatrixVisual(nodes, contentY + 16, theme)
          : renderScreenVisual(nodes, contentY + 16, theme);
  const dayLabel = localizedText(day?.label) || localizedText(day?.title) || 'Training';
  return `<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" width="1200" height="675" viewBox="0 0 1200 675" role="img" aria-labelledby="title desc"><title id="title">${escapeSvg(title)}</title><desc id="desc">${escapeSvg(layout)} visual guide ${escapeSvg(slot)} for ${escapeSvg(title)}.</desc><rect width="1200" height="675" fill="#f8f6f1"/><rect x="64" y="42" width="1072" height="591" rx="30" fill="#fff" stroke="#ddd7ca"/><text x="110" y="96" fill="${theme.accent}" font-family="Arial,sans-serif" font-size="13" font-weight="700">${escapeSvg(`${dayLabel} · ${layout.toUpperCase()} WALKTHROUGH`)}</text>${svgText(titleLines, 110, 142, { size: 32, fill: theme.ink, weight: 700, lineHeight: 38, family: 'Georgia,serif' })}${svgText(objectiveLines, 110, objectiveY, { size: 18, fill: '#687081', lineHeight: 24 })}${visual}<rect x="96" y="570" width="1008" height="42" rx="12" fill="#fff" stroke="#e2e8f0"/>${svgText([evidence], 120, 597, { size: 16, fill: '#687081', lineHeight: 20 })}</svg>`;
}

function createVisualSlide(chapter, day, slot) {
  const title = localizedText(chapter.title) || 'Training lesson';
  return {
    label: title,
    src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(createVisualSvg({ chapter, day, slot }))}`,
    alt: `Visual walkthrough for ${title}.`,
    description: `${localizedText(chapter.objective) || localizedText(chapter.explanation)} ${chapterEvidence(chapter)}`.trim(),
  };
}

function insertVisualBlock(chapter, day) {
  const conceptIndex = chapter.blocks.findIndex((block) => block.type === 'concept');
  const title = localizedText(chapter.title) || 'Training lesson';
  chapter.blocks.splice(conceptIndex + 1, 0, {
    id: `${chapter.id}-generated-visual-slider`,
    type: 'imageSlider',
    stage: 'understand',
    title: `${title} visual`,
    ariaLabel: `Visual walkthrough for ${title}`,
    caption: `Use this visual to keep the lesson, check, and proof in view. ${chapterEvidence(chapter)}`,
    slides: [createVisualSlide(chapter, day, 'missing')],
  });
}

export function ensureTrainingChapterVisuals(curriculum) {
  if (!curriculum?.days) return curriculum;
  const usedSources = new Set();
  curriculum.days.forEach((day) => {
    day.chapters.forEach((chapter) => {
      const visualBlocks = chapter.blocks.filter((block) => VISUAL_BLOCK_TYPES.has(block.type));
      if (!visualBlocks.length) insertVisualBlock(chapter, day);
      chapter.blocks.filter((block) => VISUAL_BLOCK_TYPES.has(block.type)).forEach((block) => {
        const items = block.type === 'imageSlider' ? block.slides : [block];
        items.forEach((item, index) => {
          if (!item?.src || usedSources.has(item.src)) {
            const replacement = createVisualSlide(chapter, day, `${block.id}-${index}`);
            Object.assign(item, replacement, item?.label ? { label: item.label } : {});
          }
          usedSources.add(item.src);
        });
      });
    });
  });
  return curriculum;
}
